import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Flag, Check, RotateCcw, User } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * Admin queue for messages that crossed the 5-report threshold.
 * Admin sees the full message text + author contact + reporter count
 * and can either APPROVE (keeps the message hidden from buyers) or
 * REVOKE (restores the message and clears reports).
 */
export default function AdminReportsPage() {
  const { user, ready } = useAuth();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reported-messages");
      setRows(r.data.messages || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load reports"));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (ready && user?.role === "admin") load(); }, [ready, user]);

  if (!ready) return <div className="max-w-4xl mx-auto px-4 py-16 text-muted-foreground">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const decide = async (msgId, action) => {
    setBusyId(msgId);
    try {
      await api.post(`/admin/messages/${msgId}/review`, { action });
      toast.success(action === "approve" ? "Kept hidden." : "Message restored.");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Action failed"));
    } finally { setBusyId(null); }
  };

  const filtered = rows.filter((r) => (tab === "pending" ? r.admin_review_status === "pending" : r.admin_review_status !== "pending"));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/admin" className="text-xs text-muted-foreground inline-flex items-center gap-1"><ArrowLeft size={12} /> Admin</Link>
      <h1 className="t-page mt-2 flex items-center gap-2"><Flag size={24} className="text-[var(--bhega-error)]" /> Reported Messages</h1>
      <p className="text-sm text-muted-foreground mt-1">Messages that received 5 or more reports are auto-hidden from buyers and queued here for your review.</p>

      <div className="mt-6 flex gap-2" data-testid="reports-tabs">
        {["pending", "history"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-none text-xs font-bold uppercase tracking-wider ${tab === t ? "bg-card text-brand" : "bg-foreground/5 text-muted-foreground"}`} data-testid={`reports-tab-${t}`}>
            {t === "pending" ? "Pending" : "History"}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {loading ? (
          <div className="text-center text-muted-foreground py-8">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-border rounded-none p-8 text-center text-sm text-muted-foreground" data-testid="reports-empty">
            No {tab} reports.
          </div>
        ) : (
          filtered.map((r) => {
            const isPending = r.admin_review_status === "pending";
            return (
              <div key={r.id} className="border border-border rounded-none bg-card p-4" data-testid={`report-row-${r.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs text-muted-foreground">
                    <strong className="text-foreground">{r.product_name}</strong>
                    {r.brand && <span> · {r.brand}</span>}
                    <span className="mx-1">·</span>
                    <span className="font-semibold text-[var(--bhega-error)] inline-flex items-center gap-1"><Flag size={11} /> {r.report_count} reports</span>
                  </div>
                  <div className={`text-xs uppercase tracking-wider font-bold px-2 py-0.5 rounded-none ${
                    r.admin_review_status === "pending" ? "bg-yellow-100 text-yellow-800" :
                    r.admin_review_status === "approved" ? "bg-[var(--bhega-error-soft)] text-[var(--bhega-error)]" :
                    "bg-[var(--bhega-success-soft)] text-[var(--bhega-success)]"
                  }`}>
                    {r.admin_review_status}
                  </div>
                </div>
                <div className="mt-3 border-l-4 border-[var(--bhega-error)] bg-[var(--bhega-error-soft)] rounded-none px-3 py-2 text-sm text-foreground" data-testid={`report-text-${r.id}`}>
                  {r.text}
                </div>
                <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                  <User size={11} /> Author: <strong className="text-foreground">{r.author_name}</strong>
                  <span>·</span><span className="font-mono">{r.author_phone}</span>
                  {r.sender_city && <><span>·</span><span>{r.sender_city}</span></>}
                  {r.sender_date && <><span>·</span><span>{r.sender_date}</span></>}
                </div>
                {isPending && (
                  <div className="mt-3 flex gap-2 justify-end">
                    <button
                      disabled={busyId === r.id}
                      onClick={() => decide(r.id, "revoke")}
                      className="btn-outline text-xs disabled:opacity-40"
                      data-testid={`report-revoke-${r.id}`}
                    >
                      <RotateCcw size={12} /> Revoke report (restore)
                    </button>
                    <button
                      disabled={busyId === r.id}
                      onClick={() => decide(r.id, "approve")}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-[var(--bhega-error)] text-foreground text-xs font-bold rounded-none hover:opacity-90 disabled:opacity-40"
                      data-testid={`report-approve-${r.id}`}
                    >
                      <Check size={12} /> Approve report (keep hidden)
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
